import type { TachoAbsence, TachoSlot } from '@oip/contracts';

/**
 * Drivers' hours as words.
 *
 * Pure and separately tested, because here the wording IS the product: every
 * figure on this card is a number of minutes, and a reader who has to do the
 * arithmetic is a reader who will get it wrong. `175` is not "nearly three
 * hours" to anybody scanning a screen.
 *
 * ⚠️ **Nothing here implements the regulations.** The device computes every
 * counter and OIP reports it. The one piece of arithmetic this module does —
 * recovering the break limit — is a SUM OF TWO NUMBERS THE DEVICE SENT, never
 * a constant chosen here, which is what keeps it correct whether the vehicle
 * runs under assimilated/AETR or GB domestic rules. Those differ, and picking
 * one would put a confident wrong number on a compliance screen.
 */

/**
 * Minutes as a person says them: `175` → `2 h 55 m`.
 *
 * Never "175 min". The counters routinely run to four figures — a weekly total
 * is 1,592 — and at that size minutes stop being a quantity anybody can picture.
 *
 * ⚠️ NULL IN, NULL OUT. A counter the device did not report must not become
 * `0 m`, which reads as a driver who has driven no time at all: on this card
 * that is the difference between "no break needed" and "we do not know". Absent
 * is not zero, at every layer.
 */
export function formatMinutes(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return null;
  // Negative is not a duration. The device should never send one; if it does,
  // rendering "-1 h" would present a fault as a measurement.
  if (minutes < 0) return null;
  const whole = Math.round(minutes);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  if (h === 0) return `${m} m`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} m`;
}

/**
 * How close this driver is to the break the DEVICE says is due.
 *
 * ⚠️ The limit is `continuous + remaining`, recovered from two numbers the
 * device sent — never the 4h30 constant. Under GB domestic rules the limit is
 * different, and hard-coding one would draw a bar that is confidently wrong for
 * every vehicle running under the other. Returns null unless BOTH are present,
 * because a bar drawn from one of them would need the missing half invented.
 */
export function breakProgress(
  slot: Pick<TachoSlot, 'continuousDriveMin' | 'timeToBreakMin'>,
): { usedMin: number; limitMin: number; pct: number } | null {
  return limitProgress(slot.continuousDriveMin, slot.timeToBreakMin);
}

/**
 * The same recovery, for any pair of "used" and "remaining".
 *
 * ⚠️ **Generalised rather than copied when the daily rest needed it.**
 * `dailyDriveMin + timeToDailyRestMin` is the daily limit the device is
 * applying, exactly as `continuous + remaining` is the break limit — so the
 * second bar is recovered by the same arithmetic and no second constant enters
 * the codebase. A hard-coded 9 hours would be confidently wrong for every
 * vehicle running under a different ruleset, which is the whole reason the
 * first one was written this way.
 */
export function limitProgress(
  usedMin: number | null,
  remainingMin: number | null,
): { usedMin: number; limitMin: number; pct: number } | null {
  if (usedMin === null || remainingMin === null) return null;
  const limit = usedMin + remainingMin;
  if (limit <= 0) return null;
  return { usedMin, limitMin: limit, pct: Math.min(100, (usedMin / limit) * 100) };
}

/**
 * What "before a break" says for one vehicle.
 *
 * ⚠️ **THREE states out of one number, and two of them are the same number.**
 * A driver at REST reports zero minutes remaining, which is arithmetically true
 * and reads as the most urgent row on the page — they have already stopped. A
 * vehicle that reported no counter also has nothing to show, and "0 m" would
 * say *stop now* about a vehicle nobody can say anything about. Both must stay
 * apart from a driver genuinely down to their last minutes.
 *
 * ⚠️ Extracted so the fleet page and the dashboard card cannot drift. They are
 * two renderings of ONE reading, and this codebase has paid for a second copy
 * of a wording rule often enough to make the shared function the default.
 */
export type BreakStatus =
  | { readonly kind: 'RESTING'; readonly label: string }
  | { readonly kind: 'UNKNOWN'; readonly label: string }
  | { readonly kind: 'DRIVING'; readonly label: string; readonly minutes: number };

export function breakStatus(
  row: Pick<TachoSlot, 'timeToBreakMin'> & { workState?: number | null },
): BreakStatus {
  // `workState === 0` is REST. Read from the device's own state rather than
  // inferred from the counter, because inferring it is exactly the merge above.
  if (row.workState === 0) return { kind: 'RESTING', label: 'On a break' };
  const left = formatMinutes(row.timeToBreakMin);
  if (left === null || row.timeToBreakMin === null || row.timeToBreakMin === undefined) {
    return { kind: 'UNKNOWN', label: 'Not reported' };
  }
  return { kind: 'DRIVING', label: left, minutes: row.timeToBreakMin };
}

/**
 * What the card in this slot means.
 *
 * ⚠️ **Slot 1 and slot 2 read differently, deliberately.** Most vehicles are
 * single-crewed, so an empty second slot is the ordinary state on most of the
 * fleet — wording it as a finding would put an alarm on every normal day and
 * teach a reader to ignore the one that matters.
 *
 * ⚠️ And the slot-1 warning carries a CAVEAT rather than an accusation.
 * ⚠️ **The caveat now appears only where it is TRUE.** AjnaView collapsed the
 * device's four-valued card state to a boolean until 14 September 2026, so
 * `false` merged *no card* — a genuine compliance finding — with *privacy
 * mode*, which is not one. The raw state arrives now, and where it does the
 * screen SAYS which it is. Where it does not — every row stored before that
 * change, and they cannot be recovered — the caveat stands, because there the
 * ambiguity is real and permanent.
 *
 * Showing it everywhere would hedge about something the platform can see;
 * dropping it everywhere would assert the worse reading on history that does
 * not support it.
 */
export function cardWording(slot: TachoSlot): {
  text: string;
  tone: 'good' | 'warning' | 'neutral';
  caveat?: string;
} {
  if (slot.cardPresent === null) return { text: 'Not reported', tone: 'neutral' };
  if (slot.cardPresent) return { text: 'Card inserted', tone: 'good' };

  /*
   * ⚠️ **Privacy mode is NOT a finding**, so it is neutral and never amber. A
   * driver who is legitimately out of scope must not be shown in the same tone
   * as one driving with no card — that is the accusation this whole change
   * exists to stop making.
   */
  if (slot.cardState === 3) {
    return { text: 'Privacy mode', tone: 'neutral' };
  }
  if (slot.slot === 2) return { text: 'No second card', tone: 'neutral' };

  /* The device said 0: no card, and it is certain rather than inferred. */
  if (slot.cardState === 0) return { text: 'No card inserted', tone: 'warning' };

  return {
    text: 'No card inserted',
    tone: 'warning',
    caveat:
      'This reading predates the provider sending the card state in full, so a driver who ' +
      'set privacy mode looks the same as one driving with no card.',
  };
}

/**
 * Why there is nothing to show.
 *
 * ⚠️ Three different nothings, and only one of them is a fault. A vehicle that
 * reported plenty of telemetry and no tachograph parameter simply has no
 * tachograph — normal, nothing to chase. One that reported nothing at all has a
 * device that stopped. Both draw the same empty card, and a shared message
 * sends somebody to look for a fault in a lorry that never had the part.
 */
export const TACHO_ABSENCE_WORDING: Record<TachoAbsence, string> = {
  NOT_ENTITLED: 'This organisation is not set up for tachograph data.',
  NO_TELEMETRY:
    'This vehicle has reported no telemetry at all in this period. That is a question about ' +
    'the device rather than about the tachograph.',
  NO_TACHOGRAPH:
    'This vehicle is reporting telemetry but has sent no tachograph data, so no tachograph ' +
    'appears to be fitted. Nothing to chase.',
};

/** Does this slot carry anything at all? An all-null slot is not rendered as a row of dashes. */
export function slotHasData(slot: TachoSlot): boolean {
  return (
    slot.cardPresent !== null ||
    slot.workState !== null ||
    slot.timeToBreakMin !== null ||
    slot.continuousDriveMin !== null ||
    slot.breakMin !== null ||
    slot.dailyDriveMin !== null ||
    slot.weeklyDriveMin !== null ||
    slot.timeToDailyRestMin !== null
  );
}

/**
 * The counters, in the order somebody asks them.
 *
 * Time to break FIRST — it is the single most operational fact in the payload
 * and the only one acted on within the hour. The daily and weekly totals are
 * context for planning rather than for now.
 *
 * ⚠️ A counter the device did not send is KEPT with a null value, not dropped.
 * Its absence is a fact about the tachograph's configuration, and a row
 * quietly missing from a compliance card reads as a figure that is fine.
 */
export function slotCounters(
  slot: TachoSlot,
): Array<{ label: string; value: string | null; emphasis?: boolean }> {
  return [
    { label: 'Before a break is due', value: formatMinutes(slot.timeToBreakMin), emphasis: true },
    { label: 'Driving without a break', value: formatMinutes(slot.continuousDriveMin) },
    { label: 'Break taken', value: formatMinutes(slot.breakMin) },
    { label: 'Driven today', value: formatMinutes(slot.dailyDriveMin) },
    { label: 'Driven this week', value: formatMinutes(slot.weeklyDriveMin) },
    { label: 'Before a daily rest is due', value: formatMinutes(slot.timeToDailyRestMin) },
  ];
}
